import React, { useState, useEffect } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Col, Row, DatePicker, Skeleton } from "antd";
import Chart from "react-apexcharts";
import { RiCalendarLine } from "react-icons/ri";
import { useIntl } from "react-intl";
import IntlMessages from "../../../../layout/components/lang/IntlMessages";
import { getNfeAnalisesFaturamentoMes } from "../../../../lib/requests";

export default function BalanceCard() {
  const customise = useSelector((state) => state.customise);
  const intl = useIntl();

  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [loadingAnalyticsMonth, setLoadingAnalyticsMonth] = useState(true);
  const [nfeListMonth, setNfeListMonth] = useState({});

  const [data, setData] = useState({
    series: [
      {
        name: intl.formatMessage({ id: "analytics-balance" }),
        data: new Array(12).fill(0),
      },
    ],
    options: {
      chart: {
        fontFamily: "Inter, sans-serif",
        type: "bar",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      dataLabels: { enabled: false },
      grid: { borderColor: "#B2BEC3", opacity: 1 },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 2,
          columnWidth: "60%",
          endingShape: "rounded",
          colors: {
            backgroundBarColors: ["#B2BEC3"],
            backgroundBarOpacity: 0.2,
          },
        },
      },
      fill: {
        opacity: 1,
        colors: [customise.theme === "light" ? "#2D3436" : "#ffffff"],
      },
      stroke: {
        show: true,
        width: 4,
        colors: ["transparent"],
      },
      xaxis: {
        categories: [
          "Jan",
          "Fev",
          "Mar",
          "Abr",
          "Mai",
          "Jun",
          "Jul",
          "Ago",
          "Set",
          "Out",
          "Nov",
          "Dez",
        ],
        labels: {
          style: {
            colors: new Array(12).fill("#B2BEC3"),
            fontSize: "12px",
          },
        },
      },
      tooltip: {
        fixed: {
          enabled: true,
          position: "topLeft",
          offsetX: 0,
          offsetY: 0,
        },
        y: {
          formatter: function (value) {
            return value.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            });
          },
        },
      },
      legend: {
        horizontalAlign: "right",
        offsetX: 40,
        position: "top",
        markers: { radius: 12 },
      },
      yaxis: {
        labels: {
          style: {
            colors: ["#636E72"],
            fontSize: "14px",
          },
          formatter: function (value) {
            return value.toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            });
          },
        },
        min: 0,
        max: 60000,
        tickAmount: 4,
      },
    },
  });

  useEffect(() => {
    const fetchNfesMonth = async () => {
      setLoadingAnalyticsMonth(true);

      const resp = await getNfeAnalisesFaturamentoMes({ ano: selectedYear });
      const item = resp.data || resp;

      if (item.error) {
        console.error("Erro ao buscar NF-e mês:", item.error.message);
        setLoadingAnalyticsMonth(false);
        return;
      }

      const formatted = {
        janeiro: parseFloat(item?.janeiro || 0),
        fevereiro: parseFloat(item?.fevereiro || 0),
        marco: parseFloat(item?.marco || 0),
        abril: parseFloat(item?.abril || 0),
        maio: parseFloat(item?.maio || 0),
        junho: parseFloat(item?.junho || 0),
        julho: parseFloat(item?.julho || 0),
        agosto: parseFloat(item?.agosto || 0),
        setembro: parseFloat(item?.setembro || 0),
        outubro: parseFloat(item?.outubro || 0),
        novembro: parseFloat(item?.novembro || 0),
        dezembro: parseFloat(item?.dezembro || 0),
        media: parseFloat(item?.media || 0),
      };

      setNfeListMonth(formatted);

      const seriesData = [
        formatted.janeiro,
        formatted.fevereiro,
        formatted.marco,
        formatted.abril,
        formatted.maio,
        formatted.junho,
        formatted.julho,
        formatted.agosto,
        formatted.setembro,
        formatted.outubro,
        formatted.novembro,
        formatted.dezembro,
      ];

      setData((prevData) => ({
        ...prevData,
        series: [
          {
            name: intl.formatMessage({ id: "analytics-balance" }),
            data: seriesData,
          },
        ],
      }));

      setLoadingAnalyticsMonth(false);
    };

    fetchNfesMonth();
  }, [selectedYear, intl]);

  const handleYearChange = (date) => {
    if (date) {
      setSelectedYear(date.year());
    }
  };

  return (
    <>
      {loadingAnalyticsMonth || data.series[0].data.length === 0 ? (
        <Col span={24}>
          <Skeleton active />
        </Col>
      ) : (
        <Row>
          <Col span={24} className="hp-mb-18">
            <Row align="middle" justify="space-between">
              <Col>
                <span className="h3 hp-font-weight-700 hp-text-color-black-bg hp-text-color-dark-0 hp-d-block">
                  <IntlMessages id="analytics-balance-month" />
                </span>
                <span className="hp-p1-body hp-mt-4 hp-mb-0">
                  {!isNaN(nfeListMonth.media)
                    ? nfeListMonth.media.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      })
                    : "0,00"}
                </span>
              </Col>

              <Col>
                <DatePicker
                  onChange={handleYearChange}
                  picker="year"
                  defaultValue={moment(`${selectedYear}`, "YYYY")}
                  suffixIcon={
                    <RiCalendarLine className="remix-icon hp-text-color-black-100" />
                  }
                />
              </Col>
            </Row>
          </Col>

          <Col span={24} style={{ overflow: "visible" }}>
            <Chart
              key={selectedYear}
              options={data.options}
              series={data.series}
              type="bar"
              width="100%"
              height={250}
              className="hp-slashed-zero"
            />
          </Col>
        </Row>
      )}
    </>
  );
}
